import { Link, useParams, useNavigate } from 'react-router-dom';
import PageTransition from '../components/PageTransition';
import ScrollReveal from '../components/ScrollReveal';
import ProjectPlaceholder from '../components/ProjectPlaceholder';
import { getProjectBySlug, projects } from '../data/projects';
import { useSEO } from '../hooks/useSEO';
import { PAGE_SEO } from '../utils/seo';

const pillColorClasses: Record<string, string> = {
  'Mobile Apps': 'pill--green',
  'Desktop Apps': 'pill--blue',
  'Web Design': 'pill--orange',
  'Logo Design': 'pill--purple',
  'Reddit Design': 'pill--red',
};

export default function ProjectDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const project = slug ? getProjectBySlug(slug) : undefined;

  // Apply dynamic SEO for project
  useSEO(project ? {
    title: `${project.title} — ${project.category} | Siak Studios`,
    description: project.longDescription.substring(0, 160),
    canonical: `/work/${slug}`,
    keywords: [...project.tags, project.category.toLowerCase(), project.client.toLowerCase(), 'case study', 'portfolio'],
    schema: {
      '@context': 'https://schema.org',
      '@type': 'CreativeWork',
      name: project.title,
      description: project.longDescription,
      image: project.images && project.images.length > 0 
        ? project.images.map(img => img.startsWith('http') ? img : `https://siak.in${img}`)
        : undefined,
      author: {
        '@type': 'Organization',
        name: 'Siak Studios',
        url: 'https://siak.in',
      },
      datePublished: project.year,
      genre: project.category,
      keywords: project.tags.join(', '),
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': `https://siak.in/work/${slug}`,
      },
    },
  } : PAGE_SEO.work);

  if (!project) {
    return (
      <PageTransition>
        <section className="section" style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ textAlign: 'center' }}>
            <h2 className="h-2" style={{ marginBottom: 'var(--space-4)' }}>
              Project not found
            </h2>
            <p className="body" style={{ color: 'var(--text-secondary)', marginBottom: 'var(--space-6)' }}>
              The project you&apos;re looking for doesn&apos;t exist.
            </p>
            <Link to="/work" className="btn btn--primary">
              View All Work
            </Link>
          </div>
        </section>
      </PageTransition>
    );
  }

  const currentIndex = projects.findIndex((p) => p.slug === project.slug);
  const nextProject = projects[(currentIndex + 1) % projects.length];
  const hasImages = project.images && project.images.length > 0;
  const hasLink = project.link && project.link !== '';

  return (
    <PageTransition>
      <div className="container">
        {/* Back Link */}
        <ScrollReveal>
          <Link to="/work" className="back-link" style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-2)', color: 'var(--text-secondary)', paddingTop: 'var(--space-8)', fontSize: '0.88rem', fontWeight: 600, marginBottom: 'var(--space-6)' }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="19" y1="12" x2="5" y2="12" />
              <polyline points="12 19 5 12 12 5" />
            </svg>
            Back to Work
          </Link>
        </ScrollReveal>

        {/* Hero */}
        <ScrollReveal>
          <div className="project-hero" style={{ marginBottom: 'var(--space-8)' }}>
            <div style={{ position: 'relative' }}>
              {hasImages ? (
                <img 
                  src={project.images![0]} 
                  alt={project.title}
                  style={{ 
                    width: '100%', 
                    maxHeight: '500px', 
                    objectFit: 'cover',
                    borderRadius: 'var(--radius-comfortable)'
                  }}
                />
              ) : (
                <ProjectPlaceholder gradient={project.gradient} letter={project.letter} size="lg" />
              )}
              <div className="project-hero__overlay">
                <h1 className="h-1" style={{ position: 'relative', zIndex: 1 }}>
                  {project.title}
                </h1>
              </div>
            </div>
          </div>
        </ScrollReveal>

        {/* MetaBar */}
        <ScrollReveal>
          <div className="project-meta" style={{ display: 'flex', gap: 'var(--space-4)', flexWrap: 'wrap', alignItems: 'center', marginBottom: 'var(--space-10)', paddingBottom: 'var(--space-6)', borderBottom: '1px solid var(--separator)' }}>
            <div className="meta-item" style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
              <span className="caption" style={{ color: 'var(--text-secondary)' }}>Client</span>
              <span className="body-bold">{project.client}</span>
            </div>
            <div className="meta-item" style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
              <span className="caption" style={{ color: 'var(--text-secondary)' }}>Year</span>
              <span className="body-bold">{project.year}</span>
            </div>
            <div className="meta-item" style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
              <span className="caption" style={{ color: 'var(--text-secondary)' }}>Category</span>
              <span className={`pill ${pillColorClasses[project.category] || ''}`}>{project.category}</span>
            </div>
            {hasLink && (
              <div className="meta-item" style={{ marginLeft: 'auto' }}>
                <a 
                  href={project.link} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="btn btn--primary btn--small"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h5" />
                    <polyline points="15 3 21 3 21 9" />
                    <line x1="10" y1="14" x2="21" y2="3" />
                  </svg>
                  View Live App
                </a>
              </div>
            )}
          </div>
        </ScrollReveal>

        {/* Image Gallery */}
        {hasImages && (
          <ScrollReveal>
            <section className="gallery-section" style={{ marginBottom: 'var(--space-10)' }}>
              <h2 className="h-3" style={{ marginBottom: 'var(--space-4)' }}>Screenshots</h2>
              <div className="gallery-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 'var(--space-4)' }}>
                {project.images!.map((img, i) => (
                  <img 
                    key={i}
                    src={img} 
                    alt={`${project.title} screenshot ${i + 1}`}
                    style={{ 
                      width: '100%', 
                      borderRadius: 'var(--radius-comfortable)',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
                    }}
                    loading="lazy"
                  />
                ))}
              </div>
            </section>
          </ScrollReveal>
        )}

        {/* Content Sections */}
        <ScrollReveal>
          <div className="project-content" style={{ maxWidth: '800px', margin: '0 auto' }}>
            <section className="content-section" style={{ marginBottom: 'var(--space-10)' }}>
              <h2 className="h-3" style={{ marginBottom: 'var(--space-3)', color: 'var(--accent-green)' }}>Description</h2>
              <p className="body" style={{ color: 'var(--text-near-white)', lineHeight: 1.7 }}>
                {project.longDescription}
              </p>
            </section>

            <div className="divider" style={{ marginBottom: 'var(--space-10)' }} />

            <section className="content-section" style={{ marginBottom: 'var(--space-10)' }}>
              <h2 className="h-3" style={{ marginBottom: 'var(--space-3)', color: 'var(--text-warning)' }}>Challenge</h2>
              <p className="body" style={{ color: 'var(--text-near-white)', lineHeight: 1.7 }}>
                {project.challenge}
              </p>
            </section>

            <div className="divider" style={{ marginBottom: 'var(--space-10)' }} />

            <section className="content-section" style={{ marginBottom: 'var(--space-10)' }}>
              <h2 className="h-3" style={{ marginBottom: 'var(--space-3)', color: 'var(--text-announcement)' }}>Solution</h2>
              <p className="body" style={{ color: 'var(--text-near-white)', lineHeight: 1.7 }}>
                {project.solution}
              </p>
            </section>

            <div className="divider" style={{ marginBottom: 'var(--space-10)' }} />

            <section className="content-section" style={{ marginBottom: 'var(--space-10)' }}>
              <h2 className="h-3" style={{ marginBottom: 'var(--space-3)', color: 'var(--accent-green)' }}>Result</h2>
              <p className="body" style={{ color: 'var(--text-near-white)', lineHeight: 1.7 }}>
                {project.result}
              </p>
            </section>
          </div>
        </ScrollReveal>

        {/* Next Project Navigation */}
        <ScrollReveal>
          <div className="project-nav" style={{ textAlign: 'center', paddingTop: 'var(--space-8)', paddingBottom: 'var(--space-12)', borderTop: '1px solid var(--separator)' }}>
            <p className="caption" style={{ color: 'var(--text-secondary)', marginBottom: 'var(--space-3)' }}>
              View Next Project
            </p>
            <Link
              to={`/work/${nextProject.slug}`}
              className="btn btn--primary btn--next-project"
              onClick={(e) => {
                e.preventDefault();
                navigate(`/work/${nextProject.slug}`);
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
            >
              <span className="btn--next-project__text">{nextProject.title}</span>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="btn--next-project__icon">
                <line x1="5" y1="12" x2="19" y2="12" />
                <polyline points="12 5 19 12 12 19" />
              </svg>
            </Link>
          </div>
        </ScrollReveal>
      </div>
    </PageTransition>
  );
}
